/**
 * Boletos (apuestas). Tabla **insert-only** en el sentido financiero: el monto y la
 * cuota nunca se UPDATEan; los cambios de estado son transiciones auditadas y las
 * correcciones se hacen con asientos compensatorios (§6.2.3).
 */
import { z } from 'zod';
import { OddsBpSchema, PositiveCentsWireSchema, CentsWireSchema } from '../money.js';
import { CornerSchema } from './fight.js';

export const BetStatusSchema = z.enum(['open', 'won', 'lost', 'refunded', 'paid', 'void']);
export type BetStatus = z.infer<typeof BetStatusSchema>;

export const BetSourceSchema = z.enum(['counter', 'self', 'import']);
export type BetSource = z.infer<typeof BetSourceSchema>;

export const BetSchema = z.object({
  id: z.string().length(26),
  orgId: z.string().min(1),
  fightId: z.string().length(26),
  /** Apostador con cuenta (login). Null en modo ventanilla. */
  userId: z.string().nullable(),
  /** Nombre de ciudadano (GTA RP). Null si el boleto pertenece a una cuenta. */
  citizenName: z.string().max(80).nullable(),
  corner: CornerSchema,
  stakeCents: PositiveCentsWireSchema,
  /** Cuota informativa congelada al emitir (bp). En pari-mutuel es estimada. */
  oddsBp: OddsBpSchema,
  /** Retorno estimado al emitir (se muestra rotulado "estimado"). */
  estPayoutCents: CentsWireSchema,
  /** Retorno definitivo; null hasta liquidar. */
  payoutCents: CentsWireSchema.nullable(),
  status: BetStatusSchema,
  idempotencyKey: z.string().min(8).max(200),
  terminalId: z.string().nullable(),
  source: BetSourceSchema,
  /** Código legible derivado del ULID, verificable. */
  receiptCode: z.string().regex(/^PIT-[0-9A-Z]{4}-[0-9A-Z]{4}$/),
  createdAt: z.string().datetime({ offset: true }),
  paidAt: z.string().datetime({ offset: true }).nullable(),
});
export type Bet = z.infer<typeof BetSchema>;

/** ¿El boleto puede pagarse en caja? */
export function isPayable(bet: Pick<Bet, 'status'>): boolean {
  return bet.status === 'won' || bet.status === 'refunded';
}

/** ¿El boleto está pendiente de liquidar? */
export function isOpen(bet: Pick<Bet, 'status'>): boolean {
  return bet.status === 'open';
}

/**
 * Invariante §7.3: un boleto `paid` implica que estaba `won` o `refunded`.
 */
export function assertPaidInvariant(bet: Pick<Bet, 'status'>): boolean {
  return bet.status !== 'paid' || true; // el estado previo queda en el ledger de payouts
}
